import { CircleAlert, CircleCheck, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { create } from 'zustand'
import { cn } from '@/shared/lib/cn'

export type ToastTone = 'success' | 'error'

export interface Toast {
  id: number
  tone: ToastTone
  /** Already translated. */
  message: string
}

interface ToastState {
  toasts: readonly Toast[]
  notify: (toast: Omit<Toast, 'id'>) => void
  dismiss: (id: number) => void
}

/** A notification leaves by itself after this time, unless it is hovered or focused. */
export const TOAST_DURATION_MS = 5000

// A counter, not `crypto.randomUUID`: the application also runs outside a secure context.
let nextId = 1

export const useToastStore = create<ToastState>()((set) => ({
  toasts: [],
  notify: (toast) => {
    const id = nextId
    nextId += 1
    set((state) => ({ toasts: [...state.toasts, { ...toast, id }] }))
  },
  dismiss: (id) => {
    set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) }))
  },
}))

/** Reports the outcome of an action without interrupting the user. */
export function notify(toast: Omit<Toast, 'id'>): void {
  useToastStore.getState().notify(toast)
}

export interface ToasterLabels {
  /** Accessible name of the notification area. */
  region: string
  dismiss: string
}

/** The notification area; mounted once for the whole application. */
export function Toaster({ labels }: { labels: ToasterLabels }) {
  const toasts = useToastStore((state) => state.toasts)
  const dismiss = useToastStore((state) => state.dismiss)
  return (
    // Above dialogs (z-50). The area itself lets the pointer through to the page under it.
    <section
      aria-label={labels.region}
      className="pointer-events-none fixed inset-x-4 top-4 z-[100] flex flex-col items-end gap-2"
    >
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          dismissLabel={labels.dismiss}
          onDismiss={() => {
            dismiss(toast.id)
          }}
        />
      ))}
    </section>
  )
}

const TONES = {
  success: {
    icon: CircleCheck,
    className: 'border-tone-success-border',
    iconClassName: 'text-tone-success-foreground',
  },
  error: {
    icon: CircleAlert,
    className: 'border-tone-danger-border',
    iconClassName: 'text-tone-danger-foreground',
  },
} as const

function ToastItem({
  toast,
  dismissLabel,
  onDismiss,
}: {
  toast: Toast
  dismissLabel: string
  onDismiss: () => void
}) {
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  const paused = hovered || focused
  const { id } = toast

  useEffect(() => {
    if (paused) return
    const timer = setTimeout(() => {
      useToastStore.getState().dismiss(id)
    }, TOAST_DURATION_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [paused, id])

  const tone = TONES[toast.tone]
  const Icon = tone.icon
  return (
    <div
      // An error is announced at once, a success when the screen reader is idle.
      role={toast.tone === 'error' ? 'alert' : 'status'}
      className={cn(
        'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border bg-background p-4 text-sm text-foreground shadow-lg animate-in fade-in-0 slide-in-from-top-2',
        tone.className,
      )}
      onMouseEnter={() => {
        setHovered(true)
      }}
      onMouseLeave={() => {
        setHovered(false)
      }}
      onFocus={() => {
        setFocused(true)
      }}
      onBlur={() => {
        setFocused(false)
      }}
    >
      <Icon aria-hidden className={cn('mt-0.5 size-4 shrink-0', tone.iconClassName)} />
      <p className="min-w-0 flex-1 break-words">{toast.message}</p>
      <button
        type="button"
        aria-label={dismissLabel}
        className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={onDismiss}
      >
        <X aria-hidden className="size-4" />
      </button>
    </div>
  )
}
